import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  CheckCircle2, 
  Download, 
  ArrowRight,
  Loader2,
  XCircle,
  FileText,
  Package
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StoreSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [showDownloadNotice, setShowDownloadNotice] = useState(false);

  // Check if user has seen download notice
  const hasSeenNotice = localStorage.getItem('bloomwright_download_notice_seen') === 'true';

  const orderId = searchParams.get("order_id");

  const fetchPurchaseDetails = async () => {
    try {
      const response = await axios.get(`${API}/store/purchase/${orderId}`);
      setPurchaseDetails(response.data);
      setStatus("success");
    } catch (error) {
      console.error("Error fetching purchase:", error);
      // If purchase not found, maybe payment is still processing
      if (error.response?.status === 404) {
        // Wait and retry once
        setTimeout(async () => {
          try {
            const retryResponse = await axios.get(`${API}/store/purchase/${orderId}`);
            setPurchaseDetails(retryResponse.data);
            setStatus("success");
          } catch (retryError) {
            setStatus("failed");
          }
        }, 2000);
      } else {
        setStatus("failed");
      }
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchPurchaseDetails();
    } else {
      setStatus("failed");
    }
  }, [orderId]);

  // Get PDF URL
  const getPdfUrl = () => {
    if (purchaseDetails?.readyMadePdfUrl) {
      return purchaseDetails.readyMadePdfUrl;
    }
    const brandId = purchaseDetails?.brand_id || purchaseDetails?.selected_brand_id;
    if (!brandId) return '';
    return `/api/brand-guide.pdf?brand_id=${brandId}`;
  };

  // Handle download button click
  const handleDownloadClick = () => {
    // Show notice if user hasn't seen it before
    if (!hasSeenNotice) {
      setShowDownloadNotice(true);
      return;
    }
    // Proceed with download
    triggerDownload();
  };

  // Dismiss notice and proceed with download
  const dismissNoticeAndDownload = () => {
    localStorage.setItem('bloomwright_download_notice_seen', 'true');
    setShowDownloadNotice(false);
    triggerDownload();
  };

  // Trigger the actual download - opens in new tab
  const triggerDownload = () => {
    const pdfUrl = getPdfUrl();
    if (!pdfUrl) return;
    window.open(pdfUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Global Download Notice - shown once per user */}
      {showDownloadNotice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#8DA399]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-[#8DA399]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2C332E] mb-3">
                Your Brand Guide Is Ready
              </h3>
              <p className="text-[#78716C] mb-6 leading-relaxed">
                Your complete brand guide has been prepared and is ready to view.
              </p>
              <p className="text-[#78716C] mb-6 leading-relaxed text-sm">
                Open the guide in a new tab to review, save, or download it when you're ready.
              </p>
              <button 
                onClick={dismissNoticeAndDownload}
                className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-3 font-medium w-full"
                data-testid="open-pdf-btn"
              >
                Open Brand Guide (PDF)
              </button>
              <p className="text-xs text-[#A8A29E] mt-4 leading-relaxed">
                You can download the PDF directly from your browser once it opens.
                Opening in a new tab ensures uninterrupted access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-20">
        {status === "checking" && (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-[#8DA399]/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#8DA399] animate-spin" />
            </div>
            <h1 className="text-3xl font-semibold text-[#2C332E] mb-4">
              Verifying Your Payment
            </h1>
            <p className="text-[#78716C]">
              Please wait while we confirm your purchase...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-[#8DA399]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#8DA399]" />
            </div>
            <h1 className="text-4xl font-semibold text-[#2C332E] mb-4">
              Your brand is now active.
            </h1>
            <p className="text-lg text-[#78716C] mb-2">
              Take your time. Nothing here is urgent.
            </p>
            <p className="text-sm text-[#8DA399] mb-8">
              Your brand package is ready to download below.
            </p>

            {/* Purchase Details Card */}
            {purchaseDetails && (
              <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#C77D63]/10 flex items-center justify-center">
                      <Package className="w-6 h-6 text-[#C77D63]" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-[#2C332E]">
                        {purchaseDetails.package_name || 'Brand Package'}
                      </h3>
                      <p className="text-sm text-[#78716C]">
                        Order ID: {purchaseDetails.order_id?.slice(0, 12)}...
                      </p>
                    </div>
                  </div>
                  
                  {purchaseDetails.amount && (
                    <div className="flex justify-between items-center py-3 border-t border-[#E6E1DB]">
                      <span className="text-[#78716C]">Total Paid</span>
                      <span className="font-semibold text-[#2C332E]">${purchaseDetails.amount.toFixed(2)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Download PDF Card */}
            <Card className="bg-gradient-to-br from-[#8DA399]/10 to-[#C77D63]/10 rounded-2xl border-0 shadow-soft mb-8">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4 shadow-soft">
                  <FileText className="w-8 h-8 text-[#C77D63]" />
                </div>
                <h3 className="font-semibold text-[#2C332E] text-xl mb-3">Your Brand Guide Is Ready</h3>
                <p className="text-sm text-[#78716C] mb-4">
                  Your complete brand guide has been prepared and is ready to view.
                </p>
                <p className="text-sm text-[#78716C] mb-6">
                  Open the guide in a new tab to review, save, or download it when you're ready.
                </p>
                
                <button
                  onClick={handleDownloadClick}
                  className="inline-flex items-center justify-center bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-3 font-medium"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Open Brand Guide (PDF)
                </button>
                
                <p className="text-xs text-[#A8A29E] mt-4 leading-relaxed">
                  You can download the PDF directly from your browser once it opens.
                </p>
              </CardContent>
            </Card>

            {/* Pinterest Templates Card */}
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft mb-6">
              <CardContent className="p-8">
                <h3 className="font-semibold text-[#2C332E] text-lg mb-1">Pinterest Pin Templates</h3>
                <p className="text-sm text-[#8DA399] italic mb-4">Beautiful pins to share what you&apos;re building.</p>
                
                <p className="text-sm text-[#78716C] mb-4">
                  These Pinterest templates help you share your brand, products, and story in a way that feels polished and intentional.
                </p>
                <p className="text-sm text-[#78716C] italic mb-6">
                  Start with what feels right. Adjust as you go.
                </p>
                
                <p className="text-xs text-[#78716C] mb-4">
                  Click below to open the templates in Canva.<br />
                  Select <span className="font-medium">&quot;Use template&quot;</span> to create your own editable copy.
                </p>
                
                <div className="flex flex-col items-start gap-2">
                  <a 
                    href="https://www.canva.com/design/DAG9sLIGDns/doqoXoGDamqCWh3kDE2bEg/view?utm_content=DAG9sLIGDns&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#C77D63] hover:bg-[#B06A52] text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors"
                    data-testid="pinterest-template-link"
                  >
                    Open Pinterest Templates
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <p className="text-xs text-[#8DA399]">Includes 7 pin designs sized for Pinterest.</p>
                </div>
              </CardContent>
            </Card>

            {/* Instagram Templates Card */}
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft mb-8">
              <CardContent className="p-8">
                <h3 className="font-semibold text-[#2C332E] text-lg mb-1">Instagram Templates</h3>
                <p className="text-sm text-[#8DA399] italic mb-4">Designed to help you show up consistently, without pressure.</p>
                
                <p className="text-sm text-[#78716C] mb-4">
                  These Instagram post and story templates support your brand&apos;s early presence — thoughtfully, calmly, and without overwhelm.
                </p>
                <p className="text-sm text-[#78716C] mb-4">
                  Use them to introduce your brand, share a signature ritual, or communicate what your product stands for in a way that feels aligned and intentional.
                </p>
                <p className="text-sm text-[#78716C] italic mb-6">
                  Nothing here needs to be perfect. These are starting points, not rules.
                </p>
                
                <p className="text-xs text-[#78716C] mb-4">
                  Click below to open the templates in Canva.<br />
                  Select <span className="font-medium">&quot;Use template&quot;</span> to create your own editable copy.
                </p>
                
                <div className="flex flex-col items-start gap-2">
                  <a 
                    href="https://www.canva.com/design/DAG9xgqhtW8/pDPdj8VNCpxZMNxetYAZFQ/view?utm_content=DAG9xgqhtW8&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#C77D63] hover:bg-[#B06A52] text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors"
                    data-testid="instagram-template-link"
                  >
                    Open Instagram Templates
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <p className="text-xs text-[#8DA399]">Includes post and story formats sized for Instagram.</p>
                </div>
              </CardContent>
            </Card>

            {/* What's Next Card */}
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft mb-8">
              <CardContent className="p-8">
                <h3 className="font-semibold text-[#2C332E] mb-4">What&apos;s Next?</h3>
                <ul className="space-y-3 text-left">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#8DA399] mt-0.5 flex-shrink-0" />
                    <span className="text-[#78716C]">Download your PDF brand guide above</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#8DA399] mt-0.5 flex-shrink-0" />
                    <span className="text-[#78716C]">Review your color palette and brand elements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#8DA399] mt-0.5 flex-shrink-0" />
                    <span className="text-[#78716C]">Contact the recommended packaging vendors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#8DA399] mt-0.5 flex-shrink-0" />
                    <span className="text-[#78716C]">Start building your brand using the included formulas</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/dashboard")}
                className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-3"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/store")}
                className="border-[#E6E1DB] hover:bg-[#F5F0EB] text-[#2C332E] rounded-full px-8 py-3"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="text-center animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-3xl font-semibold text-[#2C332E] mb-4">
              Payment Issue
            </h1>
            <p className="text-[#78716C] mb-8">
              We couldn&apos;t verify your payment. If you were charged, please contact support.
            </p>
            <Button 
              onClick={() => navigate("/store")}
              className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-3"
            >
              Return to Store
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default StoreSuccess;
